import type {
  CustomerMappableField,
  HostedFlowStep,
  PayloadFormQuestion,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { getCustomerFieldForStep, isBuiltInHostedStep } from "./hostedStepRouting";

export type PreCollectedStep = {
  value: unknown;
  customerField: CustomerMappableField;
  valueTransform: "boolean" | "string" | "none";
};

const getValueTransform = (
  cmsQuestion: PayloadFormQuestion
): PreCollectedStep["valueTransform"] =>
  (cmsQuestion.customerFieldMapping?.valueTransform || "none") as PreCollectedStep["valueTransform"];

const BUILT_IN_IDENTITY_FIELDS_BY_STEP: Partial<Record<HostedFlowStep, CustomerMappableField[]>> = {
  contact: ["firstName", "lastName", "email"],
  phone: ["phone"],
};

export function buildPreCollectedSteps({
  activeSteps,
  answers,
  cmsQuestionMap,
}: {
  activeSteps: HostedFlowStep[];
  answers: Record<string, unknown> | undefined;
  cmsQuestionMap: Map<string, PayloadFormQuestion>;
}): Map<string, PreCollectedStep> {
  const result = new Map<string, PreCollectedStep>();
  if (!answers) return result;

  // Answers are keyed by fieldKey (Formik uses q.fieldKey as the form field id).
  // Fall back to q.id (UUID) for safety.
  const lookupAnswer = (cmsQ: PayloadFormQuestion) =>
    answers[cmsQ.fieldKey] ?? answers[cmsQ.id];

  // 1. Check dynamic hosted steps for pre-collected answers (these also get skipped in the UI).
  for (const step of activeSteps) {
    if (isBuiltInHostedStep(step)) continue;
    const customerField = getCustomerFieldForStep(step);
    const cmsQ = cmsQuestionMap.get(customerField);
    if (!cmsQ?.customerFieldMapping?.customerField) continue;

    const answerValue = lookupAnswer(cmsQ);
    if (answerValue != null && answerValue !== "") {
      result.set(step, {
        value: answerValue,
        customerField: cmsQ.customerFieldMapping.customerField as CustomerMappableField,
        valueTransform: getValueTransform(cmsQ),
      });
    }
  }

  // 2. Also collect mapped answers that don't correspond to any hosted step
  //    (e.g., DOB collected in the questionnaire but not a hosted flow step).
  const handledFields = new Set(
    Array.from(result.values()).map((entry) => entry.customerField)
  );
  const builtInIdentityFields = new Set<CustomerMappableField>(
    activeSteps.flatMap((step) => BUILT_IN_IDENTITY_FIELDS_BY_STEP[step] ?? [])
  );

  for (const [customerField, cmsQ] of cmsQuestionMap) {
    if (handledFields.has(customerField as CustomerMappableField)) continue;
    if (builtInIdentityFields.has(customerField as CustomerMappableField)) continue;
    if (!cmsQ.customerFieldMapping?.customerField) continue;

    const answerValue = lookupAnswer(cmsQ);
    if (answerValue != null && answerValue !== "") {
      result.set(customerField, {
        value: answerValue,
        customerField: cmsQ.customerFieldMapping.customerField as CustomerMappableField,
        valueTransform: getValueTransform(cmsQ),
      });
    }
  }

  return result;
}
